import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../utils/axiosConfig';

// Sözleşme onay süreci (v1.11.0) — sözleşme detayındaki durum bandı, talep farkı ve karar butonları.

export type DuzenlemeModu = 'Dogrudan' | 'Talep' | 'Kilitli';

interface TaksitOzet { taksitID: number; vadeTarihi: string; miktar: number; odemeTuruDetay?: string; odemeDurumu?: string; aciklama?: string; }
interface SozlesmeYuk { sozlesmeTarihi: string; rezerveBitisTarihi?: string | null; sozlesmeDurumu: string; sozlesmeFiyati: number; ozelSartlar?: string | null; }
interface Yuk {
  sozlesme?: SozlesmeYuk | null;
  taksitPlani?: { temizleMevcutPlan: boolean; taksitler: { vadeTarihi: string; miktar: number; odemeTuruDetay?: string; aciklama?: string }[] } | null;
  taksitGuncellemeleri?: { taksitID: number; vadeTarihi: string; miktar: number; aciklama?: string }[] | null;
  iptalEdilecekTaksitler?: number[] | null;
  silinecekTaksitler?: number[] | null;
  mevcutTaksitler?: TaksitOzet[] | null;
}
interface Talep {
  talepID: number; talepTuru: 'Onay' | 'Degisiklik' | 'Iptal'; durum: string;
  talepEdenID: number; talepEden?: string; talepTarihi: string; talepAciklamasi?: string;
  onerilen?: Yuk | null; mevcut?: Yuk | null;
  kararVeren?: string; kararTarihi?: string; kararAciklamasi?: string;
}
export interface SozlesmeOnayDurumu {
  onayDurumu: 'Bekliyor' | 'Onaylandi' | 'Reddedildi';
  sozlesmeOnayiAktif: boolean; iptalOnayiAktif: boolean; onaylayabilirMi: boolean;
  duzenlemeModu: DuzenlemeModu; iptalModu: DuzenlemeModu; kullaniciID: number;
  bekleyenTalep?: Talep | null; sonRed?: Talep | null; talepler: Talep[];
}

const para = (n?: number | null) => n == null ? '—' : `${Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
const tarih = (s?: string | null) => s ? new Date(s).toLocaleDateString('tr-TR') : '—';
const tarihSaat = (s?: string | null) => s ? new Date(s).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const TUR_ADI: Record<string, string> = { Onay: 'Sözleşme onayı', Degisiklik: 'Değişiklik talebi', Iptal: 'İptal talebi' };
const DURUM_ADI: Record<string, string> = { Bekliyor: 'Bekliyor', Onaylandi: 'Onaylandı', Reddedildi: 'Reddedildi', GeriCekildi: 'Geri çekildi' };
const DURUM_RENK: Record<string, string> = { Bekliyor: '#b45309', Onaylandi: '#15803d', Reddedildi: '#b91c1c', GeriCekildi: '#64748b' };

// ── Açıklama sorma penceresi (talep gönderirken / reddederken) ──
interface SoruSecenek { baslik: string; ozet?: string; zorunlu?: boolean; onayMetni?: string; }
export const useAciklamaSor = (): [React.ReactNode, (s: SoruSecenek) => Promise<string | null>] => {
  const [soru, setSoru] = useState<SoruSecenek | null>(null);
  const [metin, setMetin] = useState('');
  const cozRef = useRef<((v: string | null) => void) | null>(null);

  const sor = useCallback((s: SoruSecenek) => new Promise<string | null>(resolve => {
    cozRef.current = resolve;
    setMetin('');
    setSoru(s);
  }), []);

  const kapat = (v: string | null) => {
    cozRef.current?.(v);
    cozRef.current = null;
    setSoru(null);
  };

  const modal = soru && (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10050, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => kapat(null)}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.5)' }} />
      <div onClick={e => e.stopPropagation()} style={{ position: 'relative', width: '94%', maxWidth: 480, background: '#fff', borderRadius: 14, boxShadow: '0 24px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', fontSize: 15.5, fontWeight: 700, color: '#1a2440' }}>{soru.baslik}</div>
        <div style={{ padding: 18 }}>
          {soru.ozet && <div style={{ fontSize: 13, color: '#475569', marginBottom: 10, whiteSpace: 'pre-line' }}>{soru.ozet}</div>}
          <textarea value={metin} onChange={e => setMetin(e.target.value)} rows={3} autoFocus maxLength={1000}
            placeholder={soru.zorunlu ? 'Açıklama (zorunlu)' : 'Açıklama (isteğe bağlı)'}
            style={{ width: '100%', padding: '8px 11px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13.5, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={() => kapat(null)} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13.5, cursor: 'pointer' }}>Vazgeç</button>
          <button type="button" disabled={!!soru.zorunlu && !metin.trim()} onClick={() => kapat(metin.trim())}
            style={{ padding: '8px 18px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', opacity: soru.zorunlu && !metin.trim() ? 0.5 : 1 }}>
            {soru.onayMetni || 'Gönder'}
          </button>
        </div>
      </div>
    </div>
  );

  return [modal, sor];
};

// ── Değişiklik talebinin eski/yeni karşılaştırması ──
const DegisiklikFarki: React.FC<{ talep: Talep }> = ({ talep }) => {
  const yeni = talep.onerilen || {};
  const eski = talep.mevcut || {};
  const eskiTaksit = (id: number) => eski.mevcutTaksitler?.find(t => t.taksitID === id);
  const satirlar: { alan: string; eski: string; yeni: string }[] = [];

  if (yeni.sozlesme) {
    const e = eski.sozlesme, y = yeni.sozlesme;
    const ekle = (alan: string, a: string, b: string) => { if (a !== b) satirlar.push({ alan, eski: a, yeni: b }); };
    ekle('Sözleşme fiyatı', para(e?.sozlesmeFiyati), para(y.sozlesmeFiyati));
    ekle('Sözleşme tarihi', tarih(e?.sozlesmeTarihi), tarih(y.sozlesmeTarihi));
    ekle('Durum', e?.sozlesmeDurumu || '—', y.sozlesmeDurumu || '—');
    ekle('Rezerve bitiş', tarih(e?.rezerveBitisTarihi), tarih(y.rezerveBitisTarihi));
    ekle('Özel şartlar', (e?.ozelSartlar || '').trim() || '—', (y.ozelSartlar || '').trim() || '—');
  }
  (yeni.taksitGuncellemeleri || []).forEach(g => {
    const e = eskiTaksit(g.taksitID);
    satirlar.push({
      alan: `Taksit (${tarih(e?.vadeTarihi)})`,
      eski: `${tarih(e?.vadeTarihi)} · ${para(e?.miktar)}`,
      yeni: `${tarih(g.vadeTarihi)} · ${para(g.miktar)}`,
    });
  });
  [...(yeni.iptalEdilecekTaksitler || []), ...(yeni.silinecekTaksitler || [])].forEach(id => {
    const e = eskiTaksit(id);
    satirlar.push({ alan: 'Taksit kaldırılıyor', eski: `${tarih(e?.vadeTarihi)} · ${para(e?.miktar)}`, yeni: 'İptal' });
  });

  const plan = yeni.taksitPlani;
  const th: React.CSSProperties = { textAlign: 'left', padding: '6px 10px', fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' };
  const td: React.CSSProperties = { padding: '6px 10px', fontSize: 13, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {satirlar.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
          <thead><tr><th style={th}>Alan</th><th style={th}>Mevcut</th><th style={th}>Önerilen</th></tr></thead>
          <tbody>
            {satirlar.map((s, i) => (
              <tr key={i}>
                <td style={{ ...td, fontWeight: 600 }}>{s.alan}</td>
                <td style={{ ...td, color: '#64748b', textDecoration: 'line-through', whiteSpace: 'pre-line' }}>{s.eski}</td>
                <td style={{ ...td, color: '#0f172a', fontWeight: 600, whiteSpace: 'pre-line' }}>{s.yeni}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {plan && plan.taksitler?.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 8, padding: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#1e3a8a', marginBottom: 6 }}>
            {plan.temizleMevcutPlan ? 'Yeni ödeme planı (bekleyen taksitlerin yerine geçer)' : 'Plana eklenecek taksitler'}
            {' — '}{plan.taksitler.length} taksit, toplam {para(plan.taksitler.reduce((t, k) => t + Number(k.miktar || 0), 0))}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Vade</th><th style={th}>Tutar</th><th style={th}>Açıklama</th></tr></thead>
            <tbody>
              {plan.taksitler.map((k, i) => (
                <tr key={i}><td style={td}>{tarih(k.vadeTarihi)}</td><td style={td}>{para(k.miktar)}</td><td style={td}>{k.aciklama || k.odemeTuruDetay || '—'}</td></tr>
              ))}
            </tbody>
          </table>
          {plan.temizleMevcutPlan && (eski.mevcutTaksitler?.filter(t => t.odemeDurumu === 'Bekliyor').length || 0) > 0 && (
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
              Kaldırılacak bekleyen taksitler: {eski.mevcutTaksitler!.filter(t => t.odemeDurumu === 'Bekliyor').length} adet,
              toplam {para(eski.mevcutTaksitler!.filter(t => t.odemeDurumu === 'Bekliyor').reduce((t, k) => t + k.miktar, 0))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface Props {
  sozlesmeID: number;
  yenile: number;
  onDurum: (d: SozlesmeOnayDurumu | null) => void;
  onDegisti: () => void;
}

const SozlesmeOnayPaneli: React.FC<Props> = ({ sozlesmeID, yenile, onDurum, onDegisti }) => {
  const [durum, setDurum] = useState<SozlesmeOnayDurumu | null>(null);
  const [isleniyor, setIsleniyor] = useState(false);
  const [gecmisAcik, setGecmisAcik] = useState(false);
  const [aciklamaModal, aciklamaSor] = useAciklamaSor();

  const yukle = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/api/SozlesmeTalepleri/sozlesme/${sozlesmeID}`);
      setDurum(res.data);
      onDurum(res.data);
    } catch {
      // Onay bilgisi alınamazsa sayfa eski davranışla (doğrudan düzenleme) çalışmaya devam eder; backend yine korur.
      setDurum(null);
      onDurum(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sozlesmeID]);

  useEffect(() => { yukle(); }, [yukle, yenile]);

  const islem = async (fn: () => Promise<any>, basari: string) => {
    setIsleniyor(true);
    try {
      const res = await fn();
      toast.success(res?.data?.message || basari);
      await yukle();
      onDegisti();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'İşlem sırasında hata oluştu.');
    } finally {
      setIsleniyor(false);
    }
  };

  if (!durum) return null;
  const { bekleyenTalep: bt, onaylayabilirMi } = durum;
  const benimTalebim = bt && bt.talepEdenID === durum.kullaniciID;
  const surecVar = durum.sozlesmeOnayiAktif || durum.iptalOnayiAktif || durum.onayDurumu !== 'Onaylandi' || durum.talepler.length > 0;
  if (!surecVar) return null;

  const onayla = (t: Talep) => {
    if (t.talepTuru === 'Iptal' && !window.confirm('İptal talebini onaylarsanız sözleşme iptal edilir ve daire satışa açılır. Devam edilsin mi?')) return;
    islem(() => axiosInstance.post(`/api/SozlesmeTalepleri/${t.talepID}/onayla`, {}), 'Onaylandı.');
  };

  const reddet = async (t: Talep) => {
    const aciklama = await aciklamaSor({ baslik: `${TUR_ADI[t.talepTuru]} — Reddet`, ozet: 'Red gerekçesi talep eden kişiye bildirilecek.', zorunlu: true, onayMetni: 'Reddet' });
    if (aciklama == null) return;
    await islem(() => axiosInstance.post(`/api/SozlesmeTalepleri/${t.talepID}/reddet`, { aciklama }), 'Reddedildi.');
  };

  const geriCek = (t: Talep) => {
    if (!window.confirm('Talebi geri çekmek istiyor musunuz?')) return;
    islem(() => axiosInstance.post(`/api/SozlesmeTalepleri/${t.talepID}/geri-cek`), 'Talep geri çekildi.');
  };

  const tekrarGonder = async () => {
    const aciklama = await aciklamaSor({ baslik: 'Yeniden onaya gönder', ozet: 'Yaptığınız düzeltmeyi kısaca belirtebilirsiniz.', onayMetni: 'Onaya gönder' });
    if (aciklama == null) return;
    await islem(() => axiosInstance.post(`/api/SozlesmeTalepleri/sozlesme/${sozlesmeID}/tekrar-gonder`, { aciklama }), 'Sözleşme yeniden onaya gönderildi.');
  };

  const btn = (renk: string, zemin: string): React.CSSProperties => ({
    padding: '7px 14px', background: zemin, color: renk, border: `1px solid ${renk}33`, borderRadius: 8, fontSize: 13, fontWeight: 600,
    cursor: isleniyor ? 'not-allowed' : 'pointer', opacity: isleniyor ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6,
  });

  const bant = (renk: string, zemin: string, ikon: string, baslik: React.ReactNode, icerik?: React.ReactNode, butonlar?: React.ReactNode) => (
    <div style={{ border: `1px solid ${renk}55`, background: zemin, borderRadius: 12, padding: '12px 16px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: renk, fontWeight: 700, fontSize: 14 }}>
          <i className={`fas ${ikon}`}></i><span>{baslik}</span>
        </div>
        {butonlar && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{butonlar}</div>}
      </div>
      {icerik && <div style={{ marginTop: 10 }}>{icerik}</div>}
    </div>
  );

  const talepBilgisi = (t: Talep) => (
    <div style={{ fontSize: 12.5, color: '#475569' }}>
      {t.talepEden || 'Kullanıcı'} · {tarihSaat(t.talepTarihi)}
      {t.talepAciklamasi && <div style={{ marginTop: 4, color: '#334155' }}>“{t.talepAciklamasi}”</div>}
    </div>
  );

  const kararButonlari = (t: Talep) => (
    <>
      {onaylayabilirMi && <>
        <button type="button" disabled={isleniyor} onClick={() => onayla(t)} style={btn('#15803d', '#dcfce7')}><i className="fas fa-check"></i>Onayla</button>
        <button type="button" disabled={isleniyor} onClick={() => reddet(t)} style={btn('#b91c1c', '#fee2e2')}><i className="fas fa-times"></i>Reddet</button>
      </>}
      {!onaylayabilirMi && benimTalebim && t.talepTuru !== 'Onay' && (
        <button type="button" disabled={isleniyor} onClick={() => geriCek(t)} style={btn('#475569', '#fff')}><i className="fas fa-undo"></i>Geri çek</button>
      )}
    </>
  );

  let ana: React.ReactNode = null;
  if (bt?.talepTuru === 'Onay') {
    ana = bant('#b45309', '#fffbeb', 'fa-hourglass-half', 'Sözleşme proje yöneticisinin onayını bekliyor',
      <>
        {talepBilgisi(bt)}
        {!onaylayabilirMi && <div style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>Onaylanana kadar daire başkasına satılamaz. Bu sürede sözleşme ve ödeme planını düzeltebilirsiniz.</div>}
      </>,
      kararButonlari(bt));
  } else if (bt?.talepTuru === 'Degisiklik') {
    ana = bant('#1d4ed8', '#eff6ff', 'fa-pen-to-square', 'Değişiklik talebi onay bekliyor',
      <>{talepBilgisi(bt)}<div style={{ marginTop: 10 }}><DegisiklikFarki talep={bt} /></div></>,
      kararButonlari(bt));
  } else if (bt?.talepTuru === 'Iptal') {
    ana = bant('#b91c1c', '#fef2f2', 'fa-ban', 'İptal talebi onay bekliyor', talepBilgisi(bt), kararButonlari(bt));
  } else if (durum.onayDurumu === 'Reddedildi') {
    const red = durum.sonRed;
    ana = bant('#b91c1c', '#fef2f2', 'fa-circle-xmark', 'Sözleşme onayı reddedildi',
      red && (
        <div style={{ fontSize: 13, color: '#7f1d1d' }}>
          <strong>{red.kararVeren || 'Yönetici'}</strong> · {tarihSaat(red.kararTarihi)}
          {red.kararAciklamasi && <div style={{ marginTop: 4 }}>“{red.kararAciklamasi}”</div>}
          <div style={{ fontSize: 12, color: '#991b1b', marginTop: 6 }}>Sözleşme ve ödeme planını düzeltip yeniden onaya gönderebilir ya da sözleşmeyi iptal edebilirsiniz.</div>
        </div>
      ),
      <button type="button" disabled={isleniyor} onClick={tekrarGonder} style={btn('#b45309', '#fffbeb')}><i className="fas fa-paper-plane"></i>Yeniden onaya gönder</button>);
  } else if (durum.duzenlemeModu === 'Talep' || durum.iptalModu === 'Talep') {
    ana = bant('#15803d', '#f0fdf4', 'fa-shield-halved', 'Onaylı sözleşme',
      <div style={{ fontSize: 12.5, color: '#166534' }}>
        Fiyat, bilgi ve ödeme planı değişiklikleri{durum.iptalModu === 'Talep' ? ' ile iptal' : ''} proje yöneticisinin onayına gönderilir. Tahsilat işlemleri serbesttir.
      </div>);
  }

  // Bekleyen talep dışındaki son kararlar (ör. reddedilen değişiklik talebi) temsilciye görünür kalsın
  const sonKarar = durum.talepler.find(t => t.durum !== 'Bekliyor');
  const gecmis = durum.talepler.length > 0 && (
    <div style={{ marginTop: ana ? -6 : 0, marginBottom: 14 }}>
      <button type="button" onClick={() => setGecmisAcik(v => !v)} style={{ background: 'none', border: 'none', color: '#475569', fontSize: 12.5, cursor: 'pointer', padding: 0 }}>
        <i className={`fas fa-chevron-${gecmisAcik ? 'up' : 'down'}`} style={{ marginRight: 6, fontSize: 10 }}></i>
        Onay geçmişi ({durum.talepler.length})
        {!gecmisAcik && sonKarar && !bt && sonKarar.talepTuru !== 'Onay' && (
          <span style={{ marginLeft: 8, color: DURUM_RENK[sonKarar.durum] }}>· Son: {TUR_ADI[sonKarar.talepTuru]} {DURUM_ADI[sonKarar.durum]?.toLowerCase()}</span>
        )}
      </button>
      {gecmisAcik && (
        <div style={{ marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', overflow: 'hidden' }}>
          {durum.talepler.map(t => (
            <div key={t.talepID} style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', fontSize: 12.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <strong style={{ color: '#1e293b' }}>{TUR_ADI[t.talepTuru]}</strong>
                <span style={{ color: DURUM_RENK[t.durum], fontWeight: 700 }}>{DURUM_ADI[t.durum]}</span>
              </div>
              <div style={{ color: '#64748b', marginTop: 2 }}>
                {t.talepEden || 'Kullanıcı'} · {tarihSaat(t.talepTarihi)}{t.talepAciklamasi ? ` — “${t.talepAciklamasi}”` : ''}
              </div>
              {t.kararTarihi && t.durum !== 'GeriCekildi' && (
                <div style={{ color: '#475569', marginTop: 2 }}>
                  Karar: {t.kararVeren || '—'} · {tarihSaat(t.kararTarihi)}{t.kararAciklamasi ? ` — “${t.kararAciklamasi}”` : ''}
                </div>
              )}
              {t.talepTuru === 'Degisiklik' && t.durum !== 'Bekliyor' && (
                <details style={{ marginTop: 6 }}>
                  <summary style={{ cursor: 'pointer', color: '#1d4ed8' }}>Değişiklik içeriği</summary>
                  <div style={{ marginTop: 6 }}><DegisiklikFarki talep={t} /></div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      {ana}
      {gecmis}
      {aciklamaModal}
    </>
  );
};

export default SozlesmeOnayPaneli;
